import { HTTP_INTERCEPTORS, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';
import { AceNotificationService, AppCookieService } from '@ace/ace';
import { AuthService } from './core/services/authentication.service';
import { RouteUrlConstants } from './models/route-url-constants';
import { TranslateService } from '@ngx-translate/core';
import { LoaderService } from './services/loader.service';

const TOKEN_HEADER_KEY = 'authorization';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  isRefreshTokenSent = false;
  isRefreshTokenFetched = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  constructor(
    private cookieService: AppCookieService,
    private authService: AuthService,
    private route: Router,
    private loader: LoaderService,
    public toastr: AceNotificationService,
    public translate: TranslateService) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.loader.showLoader()
    let authReq = req;
    const isOtpRequest = this.isOtpRequest(req.url);
    const skipAuthHeaders = req.headers.get('skipAuthHeaders');
    req = req.clone({ headers: req.headers.delete('skipAuthHeaders') });
    if (!skipAuthHeaders && !isOtpRequest) {
      const token = this.cookieService.getCookie("accessToken");
      if (token) {
        if (token !== null) {
          authReq = this.addTokenHeader(req, token);
        }
      }
    }

    return this.handleCall(req, next, authReq);
  }

  private handleCall(req: HttpRequest<any>, next: HttpHandler, authReq: HttpRequest<any>): Observable<HttpEvent<any>> {
    return next.handle(authReq).pipe(catchError(error => {
      if (this.isOtpRequest(authReq.url) && error instanceof HttpErrorResponse && error.status === 401) {
        return throwError(error);
      }
      if (this.shouldRefresh(error, authReq.url)) {
        return this.handle401Error(authReq, next);
      } else if (error.status === 403) {
        this.route.navigateByUrl(RouteUrlConstants.FORBIDDEN);
      } else if (error.status === 500) {
        this.toastr.error('Something went wrong .....')
      }
      return throwError(error);
    }),
      finalize(() => {
        this.loader.hideLoader()
      })
    );
  }

  private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const refreshToken =    this.cookieService.getCookie("refreshToken");
    if (!this.isRefreshTokenFetched) {
      if (refreshToken) {
        this.clearCookie();
        this.isRefreshTokenSent = true;
        this.isRefreshTokenFetched = true;
        return this.authService.refreshToken(refreshToken).pipe(
          take(1),
          switchMap((tokenRes: any) => {
            this.cookieService.setCookie("accessToken", tokenRes.content.accessToken, { path: '/' });
            this.cookieService.setCookie("refreshToken", refreshToken, { path: '/' });
            this.isRefreshTokenFetched = false;
            this.refreshTokenSubject.next(tokenRes.content.accessToken);
            return this.handleCall(req, next, this.addTokenHeader(req, tokenRes.content.accessToken));

          }),
          catchError((err) => {
            this.isRefreshTokenFetched = false;
            this.isRefreshTokenSent = false;
            this.refreshTokenSubject.next(null);
            this.clearCookie();
            this.logout();
            return throwError(err);
          })
        );
      } else if (!this.isRefreshTokenSent) {
        this.clearCookie();
        this.isRefreshTokenFetched = false;
        this.logout();
        this.toastr.error(this.translate.instant("Your session has expired. Please log in again to continue"));
        return throwError("TOken is empty");
      }
    }
    else {
      // Queue requests while the token is being refreshed
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null), // Wait until the token is available
        take(1),
        switchMap((token) => {
          return this.handleCall(req, next, this.addTokenHeader(req, token));
        }) // Retry the request with the new token
      );
    }

    return throwError(() => new Error('Unhandled error scenario in handle401Error.'));

  }

  private logout() {
    this.route.navigateByUrl('login');
  }
  private clearCookie() {
    this.cookieService.deleteCookie("accessToken");
    this.cookieService.deleteCookie("refreshToken");
  }

  private addTokenHeader(request: HttpRequest<any>, token: string) {
    let headerToken: string;
    if (token) {
      if (token.startsWith("Bearer "))
        headerToken = token;
      else
        headerToken = "Bearer " + token;

      return request.clone(
        {
          setHeaders: {
            Authorization: `${headerToken}`
          }
        }
      );
    } else {
      return request.clone({ headers: request.headers.set(TOKEN_HEADER_KEY, "") });
    }
  }

  private isOtpRequest(url: string): boolean {
    return !!url && url.includes('/otp/');
  }

  private shouldRefresh(error: unknown, url: string): boolean {
    return error instanceof HttpErrorResponse
      && error.status === 401
      && !url.includes('/api/auth');
  }
}

export const authInterceptorProviders = [
  { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true }
];
